import { useReactFlow } from '@xyflow/react';
import { memo, ReactNode, useCallback, useState } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { Button, Flex, Menu, MenuItem, MenuItemDelete, Popover, Save, Text } from '@/components/core';
import { NewTriggerModal, TriggerOption } from '@/components/flow';
import { AddIcon, OverflowIcon } from '@/components/icons';
import { useAddTrigger, useDeleteTrigger } from '@/hooks';

export type TriggerHeaderProps = {
  agentId: string;
  actionId: string;
  children?: ReactNode;
};

const ITriggerHeader = ({ agentId, actionId, children }: TriggerHeaderProps) => {
  const [newTriggerModelOpened, setNewTriggerModalOpened] = useState(false);
  const builder = useReactFlow();

  const onAddTriggerSuccess = useCallback(() => {
    builder.setNodes((nodes) => nodes);
    setNewTriggerModalOpened(false);
  }, [builder]);

  const {
    addTrigger,
    isPending: isAddingTrigger,
    errors: addTriggerErrors,
  } = useAddTrigger({ onSuccess: onAddTriggerSuccess });

  const { deleteTrigger, isPending: isDeletingTrigger } = useDeleteTrigger();

  const onDeleteTrigger = useCallback(() => {
    deleteTrigger({ agentId, actionId }).then(() => {
      builder.setNodes((nodes) => nodes);
    });
  }, [deleteTrigger, agentId, actionId, builder]);

  const onTriggerOptionSelected = useCallback(
    (triggerOption: TriggerOption) => {
      addTrigger({ agentId, triggerOption }).catch(console.error);
    },
    [addTrigger, agentId]
  );

  // TODO: Figure out why uncontrolled doesn't work here
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Flex className="w-full">
      <Flex className="gap-sm grow items-center">{children}</Flex>
      {isDeletingTrigger ? (
        <Save message="Deleting" />
      ) : (
        <MenuTrigger>
          <Button variant="plain" onPress={() => setIsOpen(true)}>
            <OverflowIcon />
          </Button>

          <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
            <Menu>
              <MenuItemDelete onAction={onDeleteTrigger} />
              <MenuItem
                onAction={() => {
                  setNewTriggerModalOpened(true);
                  setIsOpen(false);
                }}
              >
                <AddIcon />
                <Text>New trigger</Text>
              </MenuItem>
            </Menu>
          </Popover>
        </MenuTrigger>
      )}
      <NewTriggerModal
        isAddingTrigger={isAddingTrigger}
        isOpen={newTriggerModelOpened}
        onOpenChange={() => setNewTriggerModalOpened(false)}
        onTriggerOptionSelected={onTriggerOptionSelected}
        errors={addTriggerErrors}
      />
    </Flex>
  );
};

export const TriggerHeader = memo(ITriggerHeader) as typeof ITriggerHeader;
